import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { 
  Shield, CheckCircle, XCircle, AlertTriangle, 
  ExternalLink, FileText, History, Lock, Code,
  ArrowRight, Building2
} from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function LicenseSupport() {
  const [licenseConfig, setLicenseConfig] = useState(null);
  const [accessLogs, setAccessLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('license');

  const user = JSON.parse(localStorage.getItem('hrms_user') || '{}');
  const isOwner = user.role === 'admin';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [configRes, logsRes] = await Promise.all([
        axios.get(`${API}/license/config`),
        isOwner ? axios.get(`${API}/license/access-logs`) : Promise.resolve({ data: [] })
      ]);
      setLicenseConfig(configRes.data);
      setAccessLogs(logsRes.data);
    } catch (error) {
      console.error('Failed to fetch license data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'vendor_login': return <History className="h-4 w-4 text-blue-500" />;
      case 'view_system_health': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'support_token_generated': return <Shield className="h-4 w-4 text-emerald-500" />;
      case 'restricted_access_violation': return <AlertTriangle className="h-4 w-4 text-red-600" />;
      default: return <History className="h-4 w-4 text-slate-500" />;
    }
  };

  const openAurousOrbit = () => {
    // Iter84 — Fall back to mailto until aurousorbit.ai has a valid
    // TLS cert. Prevents testers landing on the browser
    // "connection is not secure" page.
    window.location.href = 'mailto:support@aurousorbit.ai?subject=MyHR%20Orbit%20Support%20Request';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">License & Support</h2>
        <p className="text-slate-600 dark:text-slate-400 mt-1">
          View your perpetual license details and manage vendor support
        </p>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 border-b border-slate-200 dark:border-slate-700">
        {['license', 'validation', 'audit'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab
                ? 'text-emerald-600 border-b-2 border-emerald-600'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* License Tab */}
      {activeTab === 'license' && (
        <>
          {/* License Status Banner */}
          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-6 text-white">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <Shield className="h-6 w-6" />
                  <span className="text-emerald-100 font-medium">License Type</span>
                </div>
                <h3 className="text-2xl font-bold mb-1">Perpetual Source Code License</h3>
                <p className="text-emerald-100 text-sm">
                  You own this software perpetually. No recurring fees. Full source code access.
                </p>
              </div>
              <div className="text-right space-y-2">
                <div className="bg-white/20 rounded-lg px-4 py-2">
                  <p className="text-xs text-emerald-100">IP Transfer Status</p>
                  <p className="text-lg font-bold flex items-center justify-end">
                    <CheckCircle className="h-5 w-5 mr-1" />
                    {licenseConfig?.ip_transfer_status === 'completed' ? 'Completed' : 'Pending'}
                  </p>
                </div>
                <div className="bg-white/20 rounded-lg px-4 py-2">
                  <p className="text-xs text-emerald-100">Tenant ID</p>
                  <p className="text-xs font-mono">{licenseConfig?.tenant_id?.slice(0, 8)}...</p>
                </div>
              </div>
            </div>
          </div>

          {/* Main Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Vendor Support - Link to AurousOrbit.ai */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="h-12 w-12 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Vendor Support</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Managed by AurousOrbit.ai
                  </p>
                </div>
              </div>

              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                Vendor support includes security patches, technical assistance, and feature updates. 
                Support is optional and billed annually at 20% of your license fee.
              </p>

              <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 mb-4">
                <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Support Includes:</h4>
                <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-1">
                  <li className="flex items-center"><CheckCircle className="h-3 w-3 mr-2 text-emerald-500" /> Security patches & updates</li>
                  <li className="flex items-center"><CheckCircle className="h-3 w-3 mr-2 text-emerald-500" /> Technical assistance</li>
                  <li className="flex items-center"><CheckCircle className="h-3 w-3 mr-2 text-emerald-500" /> Bug fixes & improvements</li>
                  <li className="flex items-center"><CheckCircle className="h-3 w-3 mr-2 text-emerald-500" /> System health monitoring</li>
                </ul>
              </div>

              <Button
                onClick={openAurousOrbit}
                className="w-full bg-amber-600 hover:bg-amber-700"
                data-testid="manage-support-btn"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Manage Support at AurousOrbit.ai
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>

            {/* Data Protection Info */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="h-12 w-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Lock className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Data Protection</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Backend enforced restrictions</p>
                </div>
              </div>

              <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 mb-4">
                <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">What Vendor CAN Access:</h4>
                <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-1">
                  <li className="flex items-center"><CheckCircle className="h-3 w-3 mr-2 text-emerald-500" /> System & error logs</li>
                  <li className="flex items-center"><CheckCircle className="h-3 w-3 mr-2 text-emerald-500" /> Integration configurations (no keys)</li>
                  <li className="flex items-center"><CheckCircle className="h-3 w-3 mr-2 text-emerald-500" /> System health dashboard</li>
                  <li className="flex items-center"><CheckCircle className="h-3 w-3 mr-2 text-emerald-500" /> Controlled database repair tools</li>
                </ul>
              </div>

              <div className="bg-red-50 dark:bg-red-900/10 rounded-lg p-4">
                <h4 className="text-sm font-medium text-red-700 dark:text-red-400 mb-2">What Vendor CANNOT Access:</h4>
                <ul className="text-xs text-red-600 dark:text-red-400 space-y-1">
                  <li className="flex items-center"><XCircle className="h-3 w-3 mr-2" /> Employee database export</li>
                  <li className="flex items-center"><XCircle className="h-3 w-3 mr-2" /> Payroll reports download</li>
                  <li className="flex items-center"><XCircle className="h-3 w-3 mr-2" /> Salary modifications</li>
                  <li className="flex items-center"><XCircle className="h-3 w-3 mr-2" /> Confidential HR documents</li>
                  <li className="flex items-center"><XCircle className="h-3 w-3 mr-2" /> Raw database access / SQL</li>
                </ul>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Validation Tab */}
      {activeTab === 'validation' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Source Code Transfer */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="h-12 w-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <Code className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Source Code Transfer</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">Perpetual ownership verification</p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <span className="text-sm text-slate-600 dark:text-slate-400">Transfer Status</span>
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                  {licenseConfig?.ip_transfer_status === 'completed' ? 'COMPLETED' : 'PENDING'}
                </span>
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <span className="text-sm text-slate-600 dark:text-slate-400">License Type</span>
                <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                  PSCL (Perpetual)
                </span>
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <span className="text-sm text-slate-600 dark:text-slate-400">Recurring Fees</span>
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                  NONE
                </span>
              </div>
            </div>
          </div>

          {/* License Details */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="h-12 w-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <FileText className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">License Details</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">Your ownership rights</p>
              </div>
            </div>

            <div className="bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4">
              <h4 className="font-medium text-emerald-800 dark:text-emerald-400 mb-3">What You Own:</h4>
              <ul className="text-sm text-emerald-700 dark:text-emerald-300 space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Complete source code with full modification rights</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Deploy on any infrastructure you control</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span>No recurring license fees ever</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span>White-label and rebrand as needed</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Optional vendor support (not required)</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Audit Tab */}
      {activeTab === 'audit' && isOwner && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="h-12 w-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <History className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Vendor Access Audit Log</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">Immutable - 3 year retention (Read-only)</p>
            </div>
          </div>

          {accessLogs.length === 0 ? (
            <div className="text-center py-8">
              <History className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-2" />
              <p className="text-slate-500 dark:text-slate-400">No vendor access logs recorded</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-900">
                  <tr>
                    <th className="text-left px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300">Action</th>
                    <th className="text-left px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300">Resource</th>
                    <th className="text-left px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300">Details</th>
                    <th className="text-left px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300">Timestamp</th>
                    <th className="text-left px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300">Integrity</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {accessLogs.slice(0, 20).map(log => (
                    <tr key={log.log_id} className={`hover:bg-slate-50 dark:hover:bg-slate-700 ${
                      log.action_category === 'violation' ? 'bg-red-50 dark:bg-red-900/10' : ''
                    }`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-2">
                          {getActionIcon(log.action)}
                          <span className="text-sm text-slate-900 dark:text-slate-100">
                            {log.action.replace(/_/g, ' ')}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                        {log.resource}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400 max-w-xs truncate">
                        {log.details || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500">
                        {formatDate(log.timestamp)}
                      </td>
                      <td className="px-4 py-3">
                        {log.integrity_warning ? (
                          <span className="text-xs text-red-600">Tampered</span>
                        ) : (
                          <CheckCircle className="h-4 w-4 text-emerald-500" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Legal Protection Info */}
      <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-xl p-6">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-400 mb-2">
              Legal Protection Layer
            </h3>
            <p className="text-sm text-amber-700 dark:text-amber-300 mb-3">
              Vendor Restricted Mode is <strong>always enabled</strong> for your protection:
            </p>
            <ul className="text-sm text-amber-700 dark:text-amber-300 space-y-1">
              <li>• Vendor cannot access raw database directly - no SQL/query execution</li>
              <li>• Vendor cannot switch tenants - session scoped to single tenant</li>
              <li>• Vendor cannot clone or export tenant data - backend enforced</li>
              <li>• All vendor actions are logged and auditable (3-year retention)</li>
              <li>• Support tokens are single-use and auto-expire</li>
              <li>• Account locks after 5 failed login attempts</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
